import { memo, Suspense, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DeleteOutline, PendingActions } from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import { isArray, isBoolean, isEmpty, isNil, isObject, size } from "lodash";
import AssociationsDisplay from "../../../../components/associations/AssociationsDisplay";
import AssociationsEditor from "../../../../components/associations/AssociationsEditor";
import CreateUpdateLoanConditionForm from "../../../../components/forms/CreateUpdateLoanConditionForm";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";
import TabContent from "../../../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";

import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { htmlStringToText } from "../../../../_src_shared/utils/functions/htmlStringToText";
import LoanConditionCommentInput from "../../../../components/comments/LoanConditionCommentInput";
import FileUploadProcess from "../../../../components/processes/FileUploadProcess";
import CommentsRenderer from "../../../../components/renderers/CommentsRenderer";
import DocumentIdsRenderer from "../../../../components/renderers/DocumentIdsRenderer";
import BasicFilesTable from "../../../../components/tables/BasicFilesTable";
import Txt from "../../../../components/typography/Txt";
import { InfoStackedItem } from "../../../../components/ui/data/InfoRowItem";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { sidebarClear } from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import DrilldownSidebarPane from "../DrilldownSidebarPane";
import { amber, green, grey, red } from "@mui/material/colors";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import JsonPreview from "../../../../components/common/JsonPreview";
import HtmlEditor from "../../../../components/html/HtmlEditor";

const OBJECT_TYPE_ITEM_LABEL = "Loan Condition";

const DrilldownSidebarAddEditLoanCondition = memo(() => {
  const conditionState = useSelector((state) => state.sidebar?.state);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const condition = useMemo(() => {
    const filteredCondition = uwRequirements?.find(
      (c) => c._id === conditionState?._id,
    );
    if (isNil(filteredCondition)) {
      return;
    }
    return filteredCondition;
  }, [conditionState, uwRequirements]);

  // Edit mode only when we have an existing condition id. A sidebar-state object
  // WITHOUT an `_id` is treated as prefill data for create mode (e.g. "Create
  // condition from this document" pre-attaches the viewed document).
  const isUpdating = useMemo(
    () => isObject(condition) && !isEmpty(condition) && !!condition?._id,
    [condition],
  );
  const prefillValues = useMemo(
    () =>
      !isUpdating &&
      isObject(conditionState) &&
      !isEmpty(conditionState) &&
      !conditionState?._id
        ? conditionState
        : null,
    [isUpdating, conditionState],
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DrilldownSidebarPane
        title={
          isUpdating
            ? `Manage ${OBJECT_TYPE_ITEM_LABEL}`
            : `Add ${OBJECT_TYPE_ITEM_LABEL}`
        }
        icon={<PendingActions className="thin" />}
        initialWidth={"600px"}
        bodyPadding={isUpdating ? 0 : 2.8}
        // bodySx={{ pt: 2 }}
      >
        <LoanConditionSidebarContentRenderer
          isUpdating={isUpdating}
          condition={condition}
          prefillValues={prefillValues}
        />
      </DrilldownSidebarPane>
    </Suspense>
  );
});

// const ExpandableTabContent = ({ title, children }) => {
//   const [show, setShow] = useState(true);
//   return (
//     <Accordion
//       onChange={() => setShow(!show)}
//       expanded={show}
//       sx={{
//         m: "0 !important",
//         borderRadius: "4px",
//         boxShadow: "0 0 0px 1px #dde0e4",
//         // overflow: "hidden",
//       }}
//     >
//       <AccordionSummary
//         sx={{
//           borderBottom: "1px solid #dde0e4",
//           background: "#ffffff",
//         }}
//         expandIcon={<ExpandMoreRounded sx={{ color: "#232a31" }} />}
//       >
//         <Flx fw jb ac sx={{ color: "#232a31" }}>
//           <Txt bold variant="subtitle2" sx={{ color: "inherit" }}>
//             {title}
//           </Txt>
//         </Flx>
//       </AccordionSummary>
//       <AccordionDetails sx={{ background: "#fafaf8" }}>
//         {children}
//       </AccordionDetails>
//     </Accordion>
//   );
// };

const LoanConditionSidebarContentRenderer = ({
  isUpdating,
  condition,
  prefillValues,
}) => {
  const dispatch = useDispatch();
  const handleCreateSuccess = useCallback(() => {
    dispatch(sidebarClear());
  }, [dispatch]);

  if (isUpdating) {
    return <ManageConditionSidebarContent condition={condition} />;
  }
  return (
    <CreateUpdateLoanConditionForm
      initialValues={prefillValues}
      onSuccessFn={handleCreateSuccess}
    />
  );
};
const TAB_TYPES = {
  DETAILS: "Condition Details",
  EDIT: "Edit Condition",
  FILES: "Related Files",
  JSON: "JSON",
};

const ManageConditionSidebarContent = ({ condition }) => {
  const [tab, setTab] = useState(0);

  return (
    <Flx
      column
      sx={{
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        // borderTop: "1px solid rgb(224, 221, 213)",
        // background: "#fafaf8",
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid rgb(224, 221, 213)",
          pl: 1.5,
          background: "#ffffff",
        }}
      >
        <ToggleTabSwitcher
          tabs={[
            TAB_TYPES.DETAILS,
            TAB_TYPES.EDIT,
            `${TAB_TYPES.FILES} (${size(condition?.uploadedDocumentIds) || 0})`,
            TAB_TYPES.JSON,
          ]}
          value={tab}
          variant={"underline"}
          onChange={setTab}
          useIndex
        />
      </Box>

      <TabContent activeTab={tab} tabKey={0}>
        <ManageConditionDetailsTab data={condition} />
      </TabContent>
      <TabContent activeTab={tab} tabKey={1}>
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
          <CreateUpdateLoanConditionForm isUpdating initialValues={condition} />
        </Box>
      </TabContent>
      <TabContent activeTab={tab} tabKey={2}>
        <ManageConditionFilesTab data={condition} />
      </TabContent>
      <TabContent activeTab={tab} tabKey={3}>
        <Box sx={{ p: 2 }}>
          <JsonPreview values={condition} defaultCollapse={3} />
        </Box>
      </TabContent>

      {/* <ExpandableTabContent title={"Details"} activeTab={tab} tabKey={0}>
        <ManageConditionDetailsTab data={condition} />
      </ExpandableTabContent>
      <ExpandableTabContent title={"Edit"} activeTab={tab} tabKey={1}>
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
          <CreateUpdateLoanConditionForm isUpdating initialValues={condition} />
        </Box>
      </ExpandableTabContent>
      <ExpandableTabContent title={"Files"} activeTab={tab} tabKey={2}>
        <ManageConditionFilesTab data={condition} />
      </ExpandableTabContent> */}
    </Flx>
  );
};

const ManageConditionDetailsTab = ({ data }) => {
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;
  const associatedLoanId = loanDrilldown?._id;

  const conditionId = data?._id;
  const conditionData = uwRequirements?.find(
    (cond) => cond._id === conditionId,
  );

  const users = useSelector((state) => state?.users);

  const statusStyle = useMemo(() => {
    switch (conditionData?.status) {
      case "Second Level Review":
      case "In Progress":
        return { color: amber[900] };
      case "Rejected":
        return { color: red[800] };
      case "Accepted":
        return { color: green[800] };
      default:
        return {};
    }
  }, [conditionData?.status]);

  return (
    <>
      <Flx
        sx={{ overflow: "auto", flexGrow: 1, height: "100%", p: 2 }}
        column
        g={0.5}
      >
        <SectionBox>
          <Flx column g={2.5}>
            <FlxGrid width="150px" grow={1} gap={3}>
              <InfoStackedItem
                fontSize="11px"
                label={"Condition Status"}
                value={conditionData?.status}
                valueSx={statusStyle}
              />
              {conditionData?.updatedBy && (
                <InfoStackedItem
                  fontSize="11px"
                  label={"Last Updated By"}
                  value={`${formatDateShort(conditionData?.updateDateTime)} by ${getUserFullNameFromEmail(users, conditionData?.updatedBy)}`}
                />
              )}
              {conditionData?.commentDateTime && (
                <InfoStackedItem
                  fontSize="11px"
                  label={"Created"}
                  value={
                    conditionData?.createdBy
                      ? `${formatDateShort(conditionData?.commentDateTime)} by ${getUserFullNameFromEmail(users, conditionData?.createdBy)}`
                      : formatDateShort(conditionData?.commentDateTime)
                  }
                />
              )}
              {conditionData?.autoGenerated &&
                isNil(conditionData?.createdBy) && (
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Auto Generated"}
                    value={
                      isBoolean(conditionData?.autoGenerated) &&
                      conditionData?.autoGenerated
                        ? "Yes"
                        : "No"
                    }
                  />
                )}
              {/* <InfoStackedItem
                fontSize="11px"
                label={"Created Date"}
                value={formatDate(conditionData?.commentDateTime)}
              /> */}

              {conditionData?.dueDate && (
                <InfoStackedItem
                  hideIfEmpty
                  fontSize="11px"
                  label={"Due Date"}
                  value={formatDate(conditionData?.dueDate)}
                />
              )}
            </FlxGrid>

            {conditionData?.resolutionNotes || conditionData?.resolvedBy ? (
              <Box
                sx={{
                  background: grey[100],
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${grey[300]}`,
                }}
              >
                <Flx column gap={3} grow={1}>
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Resolved By"}
                    value={getUserFullNameFromEmail(
                      users,
                      conditionData?.resolvedBy,
                    )}
                  />
                  <InfoStackedItem
                    ellipsis={false}
                    fontSize="11px"
                    label={"Resolution Notes"} //Condition Details
                    value={htmlStringToText(conditionData?.resolutionNotes)}
                  />
                </Flx>
              </Box>
            ) : null}
          </Flx>
        </SectionBox>
        <SectionBox sx={{ gap: 1 }}>
          <Flx column g={4}>
            {conditionData?.docType ? (
              <>
                <Flx fw jb ac gap={2}>
                  <InfoStackedItem
                    fontSize="11px"
                    label={"What's Needed"} //Condition Details
                    value={data?.docType}
                  />
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Document Status"}
                    value={conditionData?.docStipStatus}
                  />
                </Flx>
                <InfoStackedItem
                  fontSize="11px"
                  label={"Details"} //Condition Details
                  value={htmlStringToText(data?.htmlString)}
                />
              </>
            ) : (
              <>
                <InfoStackedItem
                  fontSize="11px"
                  valueSx={{ fontWeight: 400 }}
                  label={"What's Needed"} //Condition Details
                  value={
                    <HtmlEditor
                      // minHeight={60}
                      value={conditionData?.htmlString} // Pass initial value only
                      suppressOuterBorder
                      readOnly
                    />
                  }
                  // value={htmlStringToText(conditionData?.htmlString)}
                />
              </>
            )}

            {!isEmpty(conditionData?.uploadedDocumentIds) ? (
              <InfoStackedItem
                ellipsis={false}
                fontSize="11px"
                label={"Uploaded Files"} //Condition Details
                value={
                  isEmpty(conditionData?.uploadedDocumentIds) ? (
                    <Txt sx={{ fontStyle: "italic" }} secondary>
                      No files uploaded yet
                    </Txt>
                  ) : (
                    <DocumentIdsRenderer
                      documents={loanDocuments}
                      documentIds={conditionData?.uploadedDocumentIds}
                    />
                  )
                }
              />
            ) : null}
          </Flx>
          <Divider sx={{ my: 2 }} />
          <Flx column g={2}>
            <Box sx={{ px: 0.5 }}>
              <CommentsRenderer
                onlyComments
                comments={
                  conditionData?.comments || conditionData?.conditionComments
                }
              />
            </Box>
            <LoanConditionCommentInput
              loanId={associatedLoanId}
              conditionId={conditionData?._id}
              inputTypeBasic
            />
          </Flx>
        </SectionBox>
        {/* <SectionBox>
          <Flx column g={2}>
            <CommentsRenderer
              onlyComments
              comments={
                conditionData?.comments || conditionData?.conditionComments
              }
            />
            <LoanConditionCommentInput
              loanId={associatedLoanId}
              conditionId={conditionData?._id}
              inputTypeBasic
            />
          </Flx>
        </SectionBox> */}
        <Divider sx={{ my: 3 }}>
          <Txt secondary>Additional Condition Details</Txt>
        </Divider>
        <SectionBox>
          <FlxGrid width="150px" grow={1} gap={3}>
            <InfoStackedItem
              fontSize="11px"
              label={"Last Updated"}
              value={formatDate(conditionData?.updateDateTime)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Last Updated By"}
              value={getUserFullNameFromEmail(users, conditionData?.updatedBy)}
            />

            <InfoStackedItem
              fontSize="11px"
              label={"Requirement Type"}
              value={conditionData?.requirementType}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Created Date/Time"}
              value={formatDateTime(conditionData?.commentDateTime)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Created By"}
              value={getUserFullNameFromEmail(users, conditionData?.createdBy)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Display Externally"}
              value={
                isBoolean(conditionData?.displayExternally) &&
                conditionData?.displayExternally
                  ? "Yes"
                  : "No"
              }
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Auto Generated"}
              value={
                isBoolean(conditionData?.autoGenerated) &&
                conditionData?.autoGenerated
                  ? "Yes"
                  : "No"
              }
            />
          </FlxGrid>
        </SectionBox>
        <Divider sx={{ my: 3 }}>
          <Txt secondary>Associations</Txt>
        </Divider>
        <SectionBox>
          <ConditionAssociationsEditor
            dealId={associatedLoanId}
            condition={conditionData}
          />
        </SectionBox>
        <DeleteConditionSection
          loanId={associatedLoanId}
          requirementId={conditionData?._id || conditionId}
        />
      </Flx>
    </>
  );
};

/**
 * Associations editor for a loan condition.
 *
 * Reuses the shared `AssociationsSelector` via its `onSave` escape hatch so
 * the full edited set of association ids is PATCHed onto the condition in a
 * single call (the shared component's default path targets loan-level add /
 * remove endpoints that don't apply here). The backend `update_underwriting_requirement`
 * endpoint `$set`s any field on the UnderwritingRequirement model, so sending
 * `{ associations: {...} }` overwrites the condition's association map.
 */
const ConditionAssociationsEditor = ({ dealId, condition }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const { updateLoanUnderwritingRequirement } = useChecklistRouteHook();
  const [editing, setEditing] = useState(false);

  const handleSave = useCallback(
    (newAssociations) => {
      if (!condition?._id) return;
      updateLoanUnderwritingRequirement({
        dealId,
        requirementId: condition._id,
        data: { associations: newAssociations },
        onSuccessMsg: "Associations updated",
        onSuccessFn: (response) => {
          let nextConditions = null;
          if (isArray(response)) nextConditions = response;
          else if (isArray(response?.conditions))
            nextConditions = response.conditions;
          else if (isArray(response?.comments))
            nextConditions = response.comments;

          if (nextConditions) {
            dispatch(loanFetchedDataUwRequirementsSet(nextConditions));
          }
        },
      });
    },
    [dealId, condition?._id, updateLoanUnderwritingRequirement, dispatch],
  );

  if (!condition?._id) return null;

  const hostLabel = condition?.docType
    ? `Condition · ${condition.docType}`
    : "Condition";

  return (
    <>
      <AssociationsDisplay
        loan={loan}
        associations={condition.associations || {}}
        onEdit={() => setEditing(true)}
      />
      <AssociationsEditor
        open={editing}
        loan={loan}
        initial={condition.associations || {}}
        hostLabel={hostLabel}
        onSave={handleSave}
        onClose={() => setEditing(false)}
      />
    </>
  );
};

/**
 * Soft-delete a condition. Two-step confirm pattern so a single click can't
 * wipe a record — click once to arm, click again to commit. On success the
 * backend returns the remaining active `conditions` plus the `archivedId`
 * (not used here; users can restore via the deleted-requirements list).
 */
const DeleteConditionSection = ({ loanId, requirementId }) => {
  const dispatch = useDispatch();
  const { loading, deleteLoanUnderwritingRequirement } =
    useChecklistRouteHook();
  const [confirming, setConfirming] = useState(false);

  const handleDelete = () => {
    deleteLoanUnderwritingRequirement({
      dealId: loanId,
      requirementId,
      onSuccessFn: (response) => {
        if (response?.conditions) {
          dispatch(loanFetchedDataUwRequirementsSet(response.conditions));
        }
        setConfirming(false);
        dispatch(sidebarClear());
      },
    });
  };

  if (isNil(loanId) || isNil(requirementId)) return null;

  if (!confirming) {
    return (
      <Flx>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
          onClick={() => setConfirming(true)}
          sx={{ textTransform: "none" }}
        >
          Delete Condition
        </Button>
      </Flx>
    );
  }
  return (
    <SectionBox
      sx={{
        background: red[50],
        borderColor: red[200],
      }}
    >
      <Flx column g={1}>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: red[900] }}>
          Delete Condition
        </Txt>
        <Txt sx={{ fontSize: 11, color: red[900] }}>
          Are you sure you want to delete this condition?
        </Txt>
        {confirming ? (
          <Flx gap={1} ac>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setConfirming(false)}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
              onClick={handleDelete}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              {loading ? "Deleting…" : "Yes, Delete Condition"}
            </Button>
          </Flx>
        ) : (
          <Flx>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
              onClick={() => setConfirming(true)}
              sx={{ textTransform: "none" }}
            >
              Delete Condition
            </Button>
          </Flx>
        )}
      </Flx>
    </SectionBox>
  );
};

const ManageConditionFilesTab = ({ data }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;

  const files = useMemo(() => {
    return loanDocuments?.filter((doc) =>
      data?.uploadedDocumentIds?.includes(doc._id),
    );
  }, [loanDocuments, data]);
  return (
    <>
      <Flx
        sx={{ overflow: "auto", flexGrow: 1, height: "100%", p: 2 }}
        column
        g={0.5}
      >
        <UploadFileToLoanConditionProcess
          loanId={loanDrilldown?._id}
          conditionId={data?._id}
          docGroup={data?.docGroup}
          docType={data?.docType}
        />
        <SectionBox sx={{ gap: 1, p: 0 }}>
          <BasicFilesTable files={files} showStatus hideIfEmpty />
        </SectionBox>
      </Flx>
    </>
    // </Flx>
  );
};

const UploadFileToLoanConditionProcess = ({
  loanId,
  conditionId,
  docType,
  docGroup,

  keyName,
}) => {
  const { loading, uploadFilesToUnderwritingRequirement } =
    useChecklistRouteHook();
  const { axiosGetIsLoading, axiosPostIsLoading, getLoanById } =
    useUnderwritingHook();
  const dispatch = useDispatch();

  const handleOnSubmit = (f, { onSuccessFn } = {}) => {
    uploadFilesToUnderwritingRequirement({
      dealId: loanId,
      requirementId: conditionId,
      files: f,
      onSuccessFn: (d) => {
        getLoanById({
          loanId,
          onSuccessFn: (data) => {
            dispatch(loanDrilldownSet(data));
            dispatch(loanFetchedDataUwRequirementsSet(d));
            onSuccessFn?.();
          },

          // onFailFn: () => dispatch(loanDrilldownSet({})),
        });
      },
    });
  };
  return (
    <Flx column g={0.5}>
      {/* <Txt>Relevant Documents</Txt> */}
      <FileUploadProcess
        loanId={loanId}
        docType={docType}
        borderSize={2}
        disableCategoryAndTypeSelection
        // disableCategoryAndTypeSelectionCount
        docGroup={docGroup}
        categoryAndTypeLabel={`Select Document Category`}
        keyName={keyName}
        onSubmit={handleOnSubmit}
        loading={loading || axiosGetIsLoading || axiosPostIsLoading}
      />
      <Divider sx={{ my: 3 }} />
    </Flx>
  );
};

const SectionBox = ({ children, sx }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${grey[300]}`,
        // border: "1px solid rgb(224, 221, 213)",
        // p: 1,
        p: 1.5,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        // gap: 4,
        mb: 2,
        background: grey[50],
        // background: "#ffffff",
        // background: "#fafaf8",
        // background: "#fafbfd",
        // background: grey[50],
        // overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
export default DrilldownSidebarAddEditLoanCondition;
